
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface Supplier {
  id: string;
  name_ar: string;
  name_en: string;
  employee_name_ar: string;
  employee_name_en: string;
  mobile: string;
  country: string;
  departments: string;
  email: string;
  access_permission: boolean;
  created_at: string;
  updated_at: string;
}

interface UseSupplierFormProps {
  supplier: Supplier | null;
  onClose: () => void;
}

export function useSupplierForm({ supplier, onClose }: UseSupplierFormProps) {
  const [formData, setFormData] = useState({
    name_ar: '',
    name_en: '',
    employee_name_ar: '',
    employee_name_en: '',
    mobile: '',
    country: '',
    departments: '',
    email: '',
    access_permission: false,
  });
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (supplier) {
      setFormData({
        name_ar: supplier.name_ar || '',
        name_en: supplier.name_en || '',
        employee_name_ar: supplier.employee_name_ar || '',
        employee_name_en: supplier.employee_name_en || '',
        mobile: supplier.mobile || '',
        country: supplier.country || '',
        departments: supplier.departments || '',
        email: supplier.email || '',
        access_permission: supplier.access_permission || false,
      });
    } else {
      setFormData({
        name_ar: '',
        name_en: '',
        employee_name_ar: '',
        employee_name_en: '',
        mobile: '',
        country: '',
        departments: '',
        email: '',
        access_permission: false,
      });
    }
  }, [supplier]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (supplier) {
        const { error } = await supabase
          .from('suppliers')
          .update({
            ...formData,
            updated_at: new Date().toISOString(),
          })
          .eq('id', supplier.id);

        if (error) throw error;

        toast({
          title: "تم التحديث",
          description: "تم تحديث المورد بنجاح",
        });
      } else {
        const { error } = await supabase
          .from('suppliers')
          .insert([formData]);

        if (error) throw error;

        toast({
          title: "تم الإضافة",
          description: "تم إضافة المورد بنجاح",
        });
      }

      onClose();
    } catch (error) {
      console.error('Error saving supplier:', error);
      toast({
        title: "خطأ",
        description: "فشل في حفظ المورد",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    formData,
    loading,
    handleInputChange,
    handleSubmit,
  };
}
